// admin-analytics-expansion P1 — Org-wide distribution charts.
//
// Renders three recharts bar charts from the org total's existing
// `AnalyticsKpi` breakdown fields (paymentStatusBreakdown / statusBreakdown /
// ticketTypeBreakdown) — no new backend field, per the P1 spec ("MUST NOT
// introduce any new backend endpoint or contract field"). Per-location rows
// also carry these breakdowns, but P1 renders only the org total's (see
// design D1/D2 — per-location breakdown charts are out of scope for this
// slice).

import { useTranslation } from '@jq4race/i18n';
import type { AnalyticsKpi } from '@jq4race/shared';

import { BarChart } from './ChartPrimitives';

export interface DistributionBarProps {
  total: AnalyticsKpi | undefined;
}

function toChartData(
  breakdown: Record<string, number> | undefined,
): { name: string; value: number }[] {
  return Object.entries(breakdown ?? {}).map(([name, value]) => ({ name, value }));
}

export function DistributionBar({ total }: DistributionBarProps) {
  const { t } = useTranslation('analytics');
  const paymentData = toChartData(total?.paymentStatusBreakdown);
  const statusData = toChartData(total?.statusBreakdown);
  const ticketTypeData = toChartData(total?.ticketTypeBreakdown);

  return (
    <div className="grid gap-6 lg:grid-cols-3">
      <DistributionCard
        title={t('distribution.paymentStatus')}
        isLoading={!total}
        data={paymentData}
      />
      <DistributionCard
        title={t('distribution.ticketStatus')}
        isLoading={!total}
        data={statusData}
      />
      <DistributionCard
        title={t('distribution.ticketType')}
        isLoading={!total}
        data={ticketTypeData}
      />
    </div>
  );
}

function DistributionCard({
  title,
  isLoading,
  data,
}: {
  title: string;
  isLoading: boolean;
  data: { name: string; value: number }[];
}) {
  const { t } = useTranslation('analytics');
  return (
    <div className="rounded-sm border border-jq-sand/70 bg-jq-surface-0 p-4 shadow-sm">
      <h3 className="text-[11px] font-medium uppercase tracking-[0.18em] text-jq-graphite">
        {title}
      </h3>
      <div className="mt-3">
        {isLoading ? (
          <div className="h-[200px] animate-pulse rounded-sm bg-jq-sand" aria-hidden />
        ) : data.length === 0 ? (
          <p className="py-8 text-center text-[13px] text-jq-stone">
            {t('emptyStates.noDataForRange')}
          </p>
        ) : (
          <BarChart
            data={data}
            xKey="name"
            series={[{ dataKey: 'value', label: title }]}
            height={200}
          />
        )}
      </div>
    </div>
  );
}
